import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Building, NavArrowDown, Plus } from "iconoir-react";
import { useOrg } from "@/lib/orgContext";

export default function OrgSwitcher() {
  const { memberships, activeOrg, activeOrgId, setActiveOrgId, refresh } = useOrg();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!memberships || memberships.length === 0) {
    return (
      <button
        data-testid="org-switcher-empty"
        onClick={() => navigate("/organizations")}
        className="flex items-center gap-2 px-3 py-1.5 border border-border text-xs font-app uppercase tracking-widest hover:bg-card transition-colors"
      >
        <Plus width={14} height={14} /> New Org
      </button>
    );
  }

  const handleSelect = (id) => {
    setActiveOrgId(id);
    setOpen(false);
    // Force a soft refresh so v1 calls pick up new tenant
    setTimeout(() => refresh(), 50);
  };

  return (
    <div className="relative" ref={ref} data-testid="org-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="org-switcher-trigger"
        className="flex items-center gap-2 px-3 py-1.5 border border-border bg-card hover:bg-card/80 transition-colors text-sm font-app max-w-[260px]"
      >
        <Building width={14} height={14} strokeWidth={1.6} />
        <span className="truncate" data-testid="org-switcher-active-name">
          {activeOrg?.name || "Select organization"}
        </span>
        <NavArrowDown width={14} height={14} strokeWidth={1.6} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 border border-border bg-background shadow-xl z-50"
          data-testid="org-switcher-menu"
        >
          <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            Workspaces
          </div>
          <div className="max-h-72 overflow-y-auto">
            {memberships.map((m) => {
              const isActive = m.organization.id === activeOrgId;
              return (
                <button
                  key={m.organization.id}
                  data-testid={`org-switcher-item-${m.organization.slug}`}
                  onClick={() => handleSelect(m.organization.id)}
                  className={`w-full text-left px-3 py-2 hover:bg-card/60 transition-colors flex items-center justify-between ${isActive ? "bg-card" : ""}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-app truncate">{m.organization.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {m.role} · {m.organization.plan_key}
                    </div>
                  </div>
                  {isActive && <span className="font-mono text-[10px] text-intl">ACTIVE</span>}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => { setOpen(false); navigate("/organizations"); }}
            data-testid="org-switcher-manage"
            className="w-full text-left px-3 py-2 border-t border-border text-xs font-app uppercase tracking-widest hover:bg-card/60 flex items-center gap-2"
          >
            <Plus width={12} height={12} /> Manage organizations
          </button>
        </div>
      )}
    </div>
  );
}
